import { Icon } from "@iconify/react";
import { Accordion, AccordionItem } from "@heroui/react";
import LeagueLogo from "./leagues/league-logo";
import CountryFlag from "./country-flags";
import FavoriteIcon from "./favorite-icon";
import { postData } from "@/utils/requests";
import TeamLogo from "./team-logo";
import { League, FavoriteLeague, popularIdsLeagues } from "./GamesList.tsx";
import { gameStates } from "@/utils/gameStates.ts";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Unlocked from "./unlocked.tsx";
import { useBypassTeamsStore } from "@/stores/bypassTeamsStore.ts";

interface FavoriteProps {
    gamesByLeagues: League[] | [];
    favoritesLeagues: FavoriteLeague[];
    setFavoritesLeagues: (favoriteLeague: FavoriteLeague[]) => void;
    favorites?: boolean | null;
};

export default function AccordionGames({ gamesByLeagues, favoritesLeagues, setFavoritesLeagues, favorites = false }: FavoriteProps) {
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(true);
    const teamsBypass = useBypassTeamsStore((state) => state.teamsBypass);

    const attFavoritesLeagues = (target_id: number | string) => {
        postData('/api/att_favorite_target', { target_type: 'league', target_id: target_id }, (data) => {
            data = data.map((fav: any) => ({ ...fav, target_id: parseInt(fav.target_id) }));
            setFavoritesLeagues(data);
        });
    };


    const formatTimestamp = (timestamp: number, t: string) => {
        const date = new Date(timestamp * 1000);
        if (t == 'date') {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } else if (t == 'time') {
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${hours}:${minutes}`;
        }
    };

    const checkBypassTeam = (team_id: number) => {
        return teamsBypass.some(t => t.team_id === team_id);
    };

    return (
        <div
            className={`${favorites && `mx-2 bg-default-200 rounded-medium shadow-medium transition-all duration-400 ${(isOpen ? 'max-h-full pb-2' : 'max-h-16')}`}`}
        >
            {favorites &&
                <div onClick={() => setIsOpen(!isOpen)} className="flex w-full items-center justify-between pl-4 pr-2 py-4 cursor-pointer">
                    <p className="text-default-700 font-medium text-lg">Favoritos</p>
                    <Icon className={`text-default-500 text-xl transition ${!isOpen && 'rotate-90'}`} icon="mdi:chevron-down" />
                </div>
            }
            <Accordion
                variant="splitted"
                selectionMode='multiple'
                defaultSelectedKeys={popularIdsLeagues.map(String) || 'all'}
            >
                {gamesByLeagues
                    .filter(league => !(favorites === false && favoritesLeagues.some(favorite => favorite.target_id === league.league_id)))
                    .filter(league => league.data.length)
                    .map((league) => (
                        <AccordionItem
                            className={`fadeIn flex flex-col w-full px-0 m-0 bg-default-100 transition-opacity duration-400 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                            key={league.league_id}
                            textValue={`${league.league_id}`}
                            keepContentMounted={true}
                            indicator={(
                                <Icon className="text-default-400 text-xl m-2" icon="mdi:chevron-left" />
                            )}
                            title={(
                                <div className={`flex w-full items-center justify-between gap-2 overflow-x-auto ${favorites ? 'pl-3' : 'pl-5'}`}>
                                    <div className='flex gap-2'>
                                        <LeagueLogo leagueId={league.league_id} size={20} className="mt-1" />
                                        <span className='text-md font-semibold text-default-800'>
                                            {league.league_name}
                                        </span>
                                    </div>
                                    <div className={`fadeIn flex items-center gap-1 ${!favorites && 'pr-0'}`}>
                                        <CountryFlag countryId={league.country_id} />
                                        <FavoriteIcon
                                            favorite={favoritesLeagues.some(favorite => favorite.target_id === league.league_id)}
                                            callback={() => attFavoritesLeagues(league.league_id)}
                                        />
                                    </div>
                                </div>
                            )}
                        >
                            {league.data.map((game) => {
                                let gameState = gameStates.find((state) => state.state_ids.includes(game.state_id))
                                let stateTitle = gameState ? t(gameState.title) : t('games_list.games_list_6');
                                return (
                                    <div key={game.id} className="flex w-full items-center my-2 py-2 gap-2 px-4 overflow-x-auto">
                                        <div className="flex flex-col items-center h-full text-default-500 text-xs pr-1 font-medium">
                                            <span>
                                                {stateTitle}
                                            </span>
                                            <span>{formatTimestamp(game.starting_at_timestamp, 'date')}</span>
                                            <span>{formatTimestamp(game.starting_at_timestamp, 'time')}</span>
                                        </div>
                                        <div className="flex flex-col gap-2 text-default-700 text-sm border-l-2 border-default-300 pl-2">
                                            <Link to={`/team/${game.home_id}`} key={game.home_id} className="flex items-center gap-2 font-medium">
                                                <TeamLogo className='mx-1' teamId={game.home_id} size={25} />
                                                <span className='clickable-dashed'>{game.home_name}</span>
                                                {checkBypassTeam(game.home_id) && (
                                                    <Unlocked />
                                                )}
                                            </Link>
                                            <Link to={`/team/${game.away_id}`} key={game.away_id} className="flex items-center gap-2 font-medium">
                                                <TeamLogo className='mx-1' teamId={game.away_id} size={25} />
                                                <span className='clickable-dashed'>{game.away_name}</span>
                                                {checkBypassTeam(game.away_id) && (
                                                    <Unlocked />
                                                )}
                                            </Link>
                                        </div>
                                        <Link to={`/game/${game.id}`} className="ml-auto p-4">
                                            <Icon className="text-primary-500 text-2xl" icon="hugeicons:football-pitch" />
                                        </Link>
                                    </div>
                                )
                            })}
                        </AccordionItem>
                    ))}
            </Accordion>
        </div>
    );
}
